import { z } from 'zod'
import { UserRole } from '~/prisma/enums'

/**
 * Roles known inside HOS.
 *
 * Updated by:
 * - XX.04.23 https://github.com/sidestream-tech/hanselmann-os/issues/128, file: "HoS_Permission_Proposal.pdf"
 * - 18.07.23 https://github.com/sidestream-tech/hanselmann-os/issues/609, file: "Rollen.xlsx"
 * - 28.08.23 https://github.com/sidestream-tech/hanselmann-os/issues/990, file: "HOS_Roles_28-08-2023" (Google Sheets)
 * - 29.11.23 https://github.com/sidestream-tech/hanselmann-os/pull/1745, for new external-access-feature
 * - 10.04.25 move to `UserRole` enum on prisma
 */
export const roles = z.nativeEnum(UserRole)

/**
 * Logged in roles
 */
export const authenticatedRoles: readonly UserRole[] = Object.values(UserRole).filter(role => role !== 'noRole')

/**
 * External roles, i.e., users that are not hanselmann employees.
 *
 */
const _externalRoles = ['noRole', 'externalServiceProjectContractor', 'externalCustomer'] satisfies Partial<UserRole>[]

/**
 * Internal roles, i.e., users that are hanselmann employees but not guests
 */
export const internalRoles: readonly UserRole[] = ['admin', 'projectManager', 'departmentLead', 'consignee', 'receptionist', 'rentalEmployee', 'salesEmployee', 'disponent', 'workshopEmployee', 'marketingEmployee', 'driver', 'bookkeeping', 'storageLead', 'storageEmployee', 'specialRoleBenni', 'serviceProjectEmployee', 'siteManager', 'serviceProjectFitter', 'logisticsEmployee', 'workshopOfficeEmployee']

/**
 * Internal and contractor-roles, used to set rights for service-projects & tasks.
 */
export const internalAndContractorRoles = [...internalRoles, 'externalServiceProjectContractor'] satisfies Partial<UserRole>[]

/**
 * Roles allowed to create, edit and delete internal reservations
 */
export const internalReservationRoles = ['departmentLead', 'projectManager', 'rentalEmployee', 'salesEmployee', 'serviceProjectEmployee', 'siteManager', 'serviceProjectFitter'] satisfies Partial<UserRole>[]

/**
 * Roles that can be invited to the system, these are people who should have access but are not part of the core-hanselmann organization.
 */
export const rolesThatCanBeInvitedToSystem = ['externalServiceProjectContractor', 'freelancerSales', 'extStorageUser', 'partnerSales', 'customerUser', 'externalServiceProjectStakeholder', 'externalWorkshopEmployee'] satisfies Partial<UserRole>[]

/**
 * Roles that have accesss to `UserTask`
 */
export const userTaskRoles = [...internalRoles, ...rolesThatCanBeInvitedToSystem] satisfies Partial<UserRole>[]

/**
 * Roles that have access to most or all data within the external storage module.
 * They can at least:
 * - switch `Customer`s when viewing data;
 * - access StoragePosition and Inbound/OutboundLogisticsTask pages;
 */
export const externalStorageInternalRoles: readonly UserRole[] = ['admin', 'departmentLead', 'storageLead', 'logisticsEmployee']

/**
 * All roles of external storage module (internal and external)
 */
export const externalStorageInternalAndExternalRoles: readonly UserRole[] = externalStorageInternalRoles.concat(['extStorageUser'])

// TODO: rename `customerModuleRoles` depending on the new module name chosen for the `customerModule`. The new name should better descripte its not-only-customer functionality, see https://github.com/sidestream-tech/hanselmann-os/issues/3542 for more
export const customerModuleRoles: readonly UserRole[] = ['customerUser', 'partnerSales']
export const customerModuleAndExternalStakeholderRoles: readonly UserRole[] = customerModuleRoles.concat(['externalServiceProjectStakeholder'])

export const internalAndCustomerUserRoles: readonly UserRole[] = internalRoles.concat(customerModuleRoles)
export const internalAndCustomerUserAndExternalStakeholderRoles: readonly UserRole[] = internalRoles.concat(customerModuleAndExternalStakeholderRoles)

/**
 * Default roles for authenticated user
 * Added from https://github.com/sidestream-tech/hanselmann-os/pull/4734
 */
export const DEFAULT_USER_ROLES: readonly UserRole[] = ['guest']

export const externalServiceProjectFullViewAccess: readonly UserRole[] = ['admin', 'departmentLead', 'bookkeeping', 'rentalEmployee', 'serviceProjectEmployee', 'serviceProjectFitter', 'siteManager']
export const internalServiceProjectFullViewAccess: readonly UserRole[] = ['admin']
